<template>
  <div id="app" class="app">
    <div class="app-viewport">
      <router-view />
    </div>
    <ToastContainer />
    <GlobalLoader />
  </div>
</template>

<script setup lang="ts">
import { onMounted, provide, watch } from "vue";
import { ensureSharedSession } from "@/composables/useSharedAuth";
import { useI18n } from "vue-i18n";
import { apiLanguage } from "@/i18n";
import ToastContainer from "@/components/ToastContainer.vue";
import GlobalLoader from "@/components/GlobalLoader.vue";
import { useKeyboardShortcuts } from "@/composables/useKeyboardShortcuts";
import { initializeUndoRedo } from "@/utils/initUndoRedo";
import { useKeywordsStore } from "@/stores/keywords.store";
import { useAuthStore } from "@/stores/auth.store";
import sessionTrackingService from "@/services/sessionTrackingService";

// Композаблы
const { locale, t } = useI18n();
const authStore = useAuthStore();

// Инициализация undo/redo
initializeUndoRedo(() => useKeywordsStore());

// Подключаем глобальные горячие клавиши (должно вызываться в setup, не в onMounted)
useKeyboardShortcuts();

// Session tracking initialization
watch(
  () => authStore.isAuthenticated,
  async (isAuthenticated) => {
    if (isAuthenticated && authStore.user) {
      try {
        console.log("📊 Initializing session tracking for authenticated user");
        await sessionTrackingService.init();
      } catch (error) {
        console.error("📊 Failed to initialize session tracking:", error);
      }
    } else {
      // User logged out - end session gracefully in background
      // Не блокируем logout процесс, если завершение сессии упадет
      sessionTrackingService.endSession().catch((error) => {
        console.error("📊 Failed to end session:", error);
      });
      sessionTrackingService.destroy();
      console.log("📊 Session tracking ended for logged out user");
    }
  },
  { immediate: true },
);

// Настройка языка документа + відновлення сесії (cookie) для всього SPA, включно з Anchor Plan
// Заголовок вкладки ставить роутер (meta.title) — тут лише запасний, якщо його ще немає.
watch(
  locale,
  (lang) => {
    document.documentElement.lang = apiLanguage(lang);
  },
  { immediate: true },
);

onMounted(async () => {
  if (!document.title) document.title = t("app.title");
  await ensureSharedSession();
});

// Предоставляем глобальные сервисы
provide("app", {
  name: "Semantica Promodo",
  version: "1.0.0",
});
</script>

<style lang="scss">
.app {
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: $color-bg-dark;

  // Градиентные акценты как на изображении
  background-image:
    radial-gradient(
      circle at 20% 50%,
      rgba(232, 65, 66, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(232, 65, 66, 0.05) 0%,
      transparent 50%
    );

  // Анимация появления
  animation: fade-in 0.3s ease-out;
}

.app-viewport {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

// Прелоадер
.app-loading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: $color-bg-dark;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;

  .loading-content {
    text-align: center;

    .loading-logo {
      font-family: $font-family-heading;
      font-size: $font-size-3xl;
      font-weight: $font-weight-bold;
      background: $gradient-primary;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      margin-bottom: $space-lg;
    }

    .loading-spinner {
      width: 40px;
      height: 40px;
      margin: 0 auto;
      border: 3px solid $color-border;
      border-radius: 50%;
      border-top-color: $color-primary;
      animation: spin 1s linear infinite;
    }

    .loading-text {
      margin-top: $space-lg;
      color: $color-text-secondary;
      font-size: $font-size-sm;
    }
  }
}

// Глобальные переходы между страницами
.page-enter-active,
.page-leave-active {
  transition: all 0.3s ease-out;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

// Адаптивность для мобильных устройств
@media (max-width: $breakpoint-sm) {
  .app {
    background-image: radial-gradient(
      circle at 50% 20%,
      rgba(232, 65, 66, 0.08) 0%,
      transparent 40%
    );
  }
}
</style>
